import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "primereact/button";
import { Panel } from "primereact/panel";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDateTime } from "@/lib/utils";
import { getToastStateFromLocationState } from "@/lib/navigation";
import { BackButton } from "@/components/BackButton";
import { useToast, confirmAsync } from "@/lib/ui";
import { parseApiError } from "@/lib/error-handling";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import {
  useAdminUser,
  useAdminUserUpdate,
  useAdminUserResetPassword,
  useAdminParticipantsList,
  useAdminCheckoutsList,
  eqFilter,
  type AdminUserDetail,
  type AdminParticipantListItem,
  type AdminCheckoutListItem,
} from "@/lib/admin-api";

function StatusBadge({ status }: { status: string | undefined }) {
  const getSeverity = (status: string | undefined) => {
    switch (status) {
      case "PENDING":
        return "warning";
      case "APPROVED":
        return "success";
      case "REJECTED":
        return "danger";
      case "CANCELLED":
        return "info";
      default:
        return null;
    }
  };

  if (!status) return null;

  return <Tag value={status} severity={getSeverity(status)} />;
}

type ParticipantWithEvent = AdminParticipantListItem & {
  event?: { id: string; title: string; startsAt: string | null } | null;
};

export function UserDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const { toastRef, showToast } = useToast();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [user, setUser] = useState<Partial<AdminUserDetail>>({
    firstName: "",
    lastName: "",
    email: "",
    isAdmin: false,
  });
  const [submitted, setSubmitted] = useState(false);

  const { lazyParams, onPage, onSort } = useLazyTableParams({
    initialRows: 10,
    initialSortField: "createdAt",
    initialSortOrder: -1,
  });

  const { lazyParams: checkoutsLazyParams, onPage: onCheckoutsPage, onSort: onCheckoutsSort } = useLazyTableParams({
    initialRows: 10,
    initialSortField: "createdAt",
    initialSortOrder: -1,
  });

  const userQuery = useAdminUser(id);
  const userData = (userQuery.data as { user?: AdminUserDetail } | undefined)?.user ?? null;

  // Refetch user data after update
  const userUpdate = useAdminUserUpdate();

  const resetPassword = useAdminUserResetPassword();

  // Fetch participants for this user using RSQL filter
  const participantsFilter = id ? eqFilter("userId", id) : undefined;
  const participantsQuery = useAdminParticipantsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filter: participantsFilter,
  });
  const participants = (participantsQuery.data?.data ?? []) as ParticipantWithEvent[];
  const totalRecords = participantsQuery.data?.pagination.total ?? 0;
  const participantsLoading = participantsQuery.isFetching;

  // Fetch checkouts for this user using RSQL filter
  const checkoutsFilter = id ? eqFilter("userId", id) : undefined;
  const checkoutsQuery = useAdminCheckoutsList({
    page: checkoutsLazyParams.page + 1,
    pageSize: checkoutsLazyParams.rows,
    sortField: checkoutsLazyParams.sortField,
    sortOrder: checkoutsLazyParams.sortOrder,
    filter: checkoutsFilter,
  });
  const checkouts = (checkoutsQuery.data?.data ?? []) as AdminCheckoutListItem[];
  const checkoutsTotalRecords = checkoutsQuery.data?.pagination.total ?? 0;
  const checkoutsLoading = checkoutsQuery.isFetching;

  useEffect(() => {
    if (!id) {
      void navigate("/users");
    }
  }, [id, navigate]);

  // Show toast from router state
  useEffect(() => {
    if (userQuery.isLoading) return;

    const toastState = getToastStateFromLocationState(location.state);
    if (!toastState) return;

    showToast(toastState);
    void navigate(location.pathname, { replace: true, state: {} });
  }, [
    location.pathname,
    location.state,
    navigate,
    userQuery.isLoading,
    showToast,
  ]);

  const handleEdit = () => {
    if (userData) {
      setUser({
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        isAdmin: userData.isAdmin,
      });
      setEditDialogVisible(true);
    }
  };

  const handleSave = async () => {
    if (!id || !user.firstName?.trim() || !user.lastName?.trim() || !user.email?.trim()) {
      setSubmitted(true);
      return;
    }

    try {
      await userUpdate.mutateAsync({
        params: { path: { id } },
        body: {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isAdmin: user.isAdmin ?? false,
        },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: "User updated",
        life: 3000,
      });
      setEditDialogVisible(false);
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to update user",
        life: 3000,
      });
    }
  };

  const handleResetPassword = async () => {
    if (!id || !userData?.email) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "User does not have an email address",
        life: 3000,
      });
      return;
    }

    try {
      await resetPassword.mutateAsync({
        params: { path: { id } },
      });
      showToast({
        severity: "success",
        summary: "Successful",
        detail: `Password reset email sent to ${userData.email}`,
        life: 5000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to reset password",
        life: 3000,
      });
    }
  };

  const handleToggleAdmin = async () => {
    if (!id || !userData) return;

    const newAdminStatus = !userData.isAdmin;
    const action = newAdminStatus ? "grant" : "revoke";
    const confirmed = await confirmAsync({
      message: `Are you sure you want to ${action} admin rights for ${userData.firstName} ${userData.lastName}?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: newAdminStatus ? "Grant Admin" : "Revoke Admin",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
    });

    if (!confirmed) return;

    try {
      await userUpdate.mutateAsync({
        params: { path: { id } },
        body: {
          isAdmin: newAdminStatus,
        },
      });
      // Refetch user data to update the UI
      await userQuery.refetch();
      showToast({
        severity: "success",
        summary: "Successful",
        detail: `Admin rights ${newAdminStatus ? "granted" : "revoked"}`,
        life: 3000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: message || "Failed to update admin status",
        life: 3000,
      });
    }
  };

  if (!userData) {
    return (
      <div className="card">
        <p>Loading...</p>
      </div>
    );
  }

  const eventBodyTemplate = (rowData: ParticipantWithEvent) => {
    const event = rowData.event;
    if (!event) return "-";
    const startDate = event.startsAt
      ? formatDateTime(new Date(event.startsAt))
      : "-";
    return (
      <div>
        <div>{event.title}</div>
        <small className="text-gray-500">{startDate}</small>
      </div>
    );
  };

  const statusBodyTemplate = (rowData: ParticipantWithEvent) => {
    return <StatusBadge status={rowData.status} />;
  };

  const amountBodyTemplate = (rowData: ParticipantWithEvent) => {
    if (!rowData.paymentAmountCents) return "Free";
    const amount = rowData.paymentAmountCents / 100;
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(amount);
  };

  const actionBodyTemplate = (rowData: ParticipantWithEvent) => {
    return (
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-sm"
        onClick={() => navigate(`/participants/${rowData.id}`)}
        tooltip="View"
        tooltipOptions={{ position: "top" }}
      />
    );
  };

  // Checkout body templates
  const checkoutStatusBadge = (status: string | undefined) => {
    const getSeverity = (status: string | undefined) => {
      switch (status) {
        case "PENDING":
          return "warning";
        case "APPROVED":
          return "success";
        case "CANCELLED":
          return "info";
        case "PARTIAL":
          return "info";
        case "MIXED":
          return "warning";
        case "COMPLETE":
          return "success";
        default:
          return null;
      }
    };

    if (!status) return null;

    return <Tag value={status} severity={getSeverity(status)} />;
  };

  const checkoutBookingReferenceBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return rowData.bookingReference || "-";
  };

  const checkoutStatusBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return checkoutStatusBadge(rowData.calculatedStatus);
  };

  const checkoutTotalAmountBodyTemplate = (rowData: AdminCheckoutListItem) => {
    const amount = rowData.totalCents ?? 0;
    if (amount === 0) return "Free";
    const currency = rowData.currency ?? "gbp";
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const checkoutActionBodyTemplate = (rowData: AdminCheckoutListItem) => {
    return (
      <Button
        icon="pi pi-eye"
        className="p-button-text p-button-sm"
        onClick={() => navigate(`/checkouts/${rowData.id}`)}
        tooltip="View"
        tooltipOptions={{ position: "top" }}
      />
    );
  };

  const editDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setEditDialogVisible(false)}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void handleSave();
        }}
      />
    </>
  );

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <div className="mb-4">
        <div className="flex align-items-center gap-3 mb-4">
          <BackButton />
          <h1 className="text-2xl font-bold m-0">
            {userData.firstName} {userData.lastName}
          </h1>
        </div>
      </div>

      <Panel header="User Information" className="mb-4">
        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="mb-3">
              <strong>Name:</strong> {userData.firstName} {userData.lastName}
            </div>
            <div className="mb-3">
              <strong>Email:</strong> {userData.email || "-"}
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="mb-3">
              <strong>Primary Group:</strong>{" "}
              {(() => {
                const primaryGroup = userData.groups?.find(
                  (g) => g.group.type === "Primary",
                );
                if (!primaryGroup) {
                  return <span className="text-gray-400">-</span>;
                }
                return (
                  <a
                    href={`/groups/${primaryGroup.group.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/groups/${primaryGroup.group.id}`);
                    }}
                    className="text-primary-600 hover:text-primary-800 hover:underline cursor-pointer"
                  >
                    {primaryGroup.group.name}
                  </a>
                );
              })()}
            </div>
            <div className="mb-3">
              <strong>Admin:</strong>{" "}
              {userData.isAdmin ? (
                <Tag value="Yes" severity="success" />
              ) : (
                <Tag value="No" severity="info" />
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button
            label="Edit User"
            icon="pi pi-pencil"
            onClick={handleEdit}
          />
          <Button
            label="Reset Password"
            icon="pi pi-key"
            onClick={() => {
              void handleResetPassword();
            }}
            disabled={!userData.email}
          />
          <Button
            label={userData.isAdmin ? "Revoke Admin" : "Grant Admin"}
            icon={userData.isAdmin ? "pi pi-user-minus" : "pi pi-user-plus"}
            severity={userData.isAdmin ? "warning" : "success"}
            onClick={() => {
              void handleToggleAdmin();
            }}
          />
        </div>
      </Panel>

      <Panel header="Event Participation" className="mb-4">
        <DataTable
          value={participants}
          lazy
          paginator
          rows={lazyParams.rows}
          first={lazyParams.first}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          sortField={lazyParams.sortField}
          sortOrder={lazyParams.sortOrder}
          loading={participantsLoading}
          responsiveLayout="scroll"
          emptyMessage="No event participations found"
          className="text-sm"
        >
          <Column
            field="event.title"
            header="Event"
            body={eventBodyTemplate}
            style={{ minWidth: "250px" }}
          />
          <Column
            field="status"
            header="Status"
            body={statusBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            field="paymentAmountCents"
            header="Amount"
            body={amountBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            field="createdAt"
            header="Created"
            body={(rowData) => formatDateTime(rowData.createdAt)}
            sortable
            style={{ minWidth: "180px" }}
          />
          <Column
            body={actionBodyTemplate}
            header="Actions"
            style={{ minWidth: "100px" }}
          />
        </DataTable>
      </Panel>

      <Panel header="Bookings" className="mb-4">
        <DataTable
          value={checkouts}
          lazy
          paginator
          rows={checkoutsLazyParams.rows}
          first={checkoutsLazyParams.first}
          totalRecords={checkoutsTotalRecords}
          onPage={onCheckoutsPage}
          onSort={onCheckoutsSort}
          sortField={checkoutsLazyParams.sortField}
          sortOrder={checkoutsLazyParams.sortOrder}
          loading={checkoutsLoading}
          responsiveLayout="scroll"
          emptyMessage="No bookings found"
          className="text-sm"
        >
          <Column
            field="bookingReference"
            header="Booking Reference"
            body={checkoutBookingReferenceBodyTemplate}
            sortable
            style={{ minWidth: "150px" }}
          />
          <Column
            field="calculatedStatus"
            header="Status"
            body={checkoutStatusBodyTemplate}
            style={{ minWidth: "120px" }}
          />
          <Column
            field="totalCents"
            header="Total Amount"
            body={checkoutTotalAmountBodyTemplate}
            sortable
            style={{ minWidth: "120px" }}
          />
          <Column
            field="createdAt"
            header="Created"
            body={(rowData) => formatDateTime(rowData.createdAt)}
            sortable
            style={{ minWidth: "180px" }}
          />
          <Column
            field="submittedAt"
            header="Submitted"
            body={(rowData) => rowData.submittedAt ? formatDateTime(rowData.submittedAt) : "-"}
            sortable
            style={{ minWidth: "180px" }}
          />
          <Column
            body={checkoutActionBodyTemplate}
            header="Actions"
            style={{ minWidth: "100px" }}
          />
        </DataTable>
      </Panel>

      <Dialog
        visible={editDialogVisible}
        style={{ width: "450px" }}
        header="Edit User"
        modal
        className="p-fluid"
        footer={editDialogFooter}
        onHide={() => setEditDialogVisible(false)}
      >
        <div className="field">
          <label htmlFor="firstName">First Name</label>
          <InputText
            id="firstName"
            value={user.firstName}
            onChange={(e) => setUser({ ...user, firstName: e.target.value })}
            required
            autoFocus
            className={submitted && !user.firstName ? "p-invalid" : ""}
          />
          {submitted && !user.firstName && (
            <small className="p-error">First name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="lastName">Last Name</label>
          <InputText
            id="lastName"
            value={user.lastName}
            onChange={(e) => setUser({ ...user, lastName: e.target.value })}
            required
            className={submitted && !user.lastName ? "p-invalid" : ""}
          />
          {submitted && !user.lastName && (
            <small className="p-error">Last name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="email">Email</label>
          <InputText
            id="email"
            type="email"
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
            required
            className={submitted && !user.email ? "p-invalid" : ""}
          />
          {submitted && !user.email && (
            <small className="p-error">Email is required.</small>
          )}
        </div>
        <div className="field-checkbox">
          <input
            type="checkbox"
            id="isAdmin"
            checked={user.isAdmin ?? false}
            onChange={(e) => setUser({ ...user, isAdmin: e.target.checked })}
          />
          <label htmlFor="isAdmin">Admin</label>
        </div>
      </Dialog>
    </div>
  );
}

